import * as React from 'react';
import { Image, ImageProps, ImageSourcePropType } from 'react-native';

type FastImageSource = {
  uri?: string;
  headers?: { [key: string]: string };
  priority?: 'low' | 'normal' | 'high';
  cache?: 'immutable' | 'web' | 'cacheOnly';
};

type FastImageProps = Omit<ImageProps, 'source'> & {
  source: FastImageSource | ImageSourcePropType;
  resizeMode?: 'contain' | 'cover' | 'stretch' | 'center';
  onLoadStart?: () => void;
  onProgress?: (event: {
    nativeEvent: { loaded: number; total: number };
  }) => void;
  onLoad?: (event: { nativeEvent: { width: number; height: number } }) => void;
  onError?: () => void;
  onLoadEnd?: () => void;
  fallback?: boolean;
};

// Extract URI from source for comparison
const getSourceUri = (
  source: FastImageSource | ImageSourcePropType,
): string | undefined => {
  if (!source) return undefined;
  if (typeof source === 'number') return `local:${source}`;
  if (typeof source === 'object' && 'uri' in source) return source.uri;
  return undefined;
};

// Custom comparison function for React.memo
// Only re-render when meaningful props change
const arePropsEqual = (
  prevProps: FastImageProps,
  nextProps: FastImageProps,
): boolean => {
  // Compare source URI
  const prevUri = getSourceUri(prevProps.source);
  const nextUri = getSourceUri(nextProps.source);
  if (prevUri !== nextUri) return false;

  // Compare style (shallow)
  if (prevProps.style !== nextProps.style) {
    // If both are objects, do shallow comparison
    if (
      typeof prevProps.style === 'object' &&
      typeof nextProps.style === 'object' &&
      prevProps.style !== null &&
      nextProps.style !== null
    ) {
      const prevStyle = prevProps.style as Record<string, any>;
      const nextStyle = nextProps.style as Record<string, any>;
      const prevKeys = Object.keys(prevStyle);
      const nextKeys = Object.keys(nextStyle);
      if (prevKeys.length !== nextKeys.length) return false;
      for (const key of prevKeys) {
        if (prevStyle[key] !== nextStyle[key]) return false;
      }
    } else {
      return false;
    }
  }

  // Compare resizeMode
  if (prevProps.resizeMode !== nextProps.resizeMode) return false;

  // Compare key callbacks existence (not the function references)
  // We don't compare function references as they often change

  return true;
};

// Memoized FastImage component for web
const FastImageComponent = React.memo<FastImageProps>(
  ({
    source,
    resizeMode,
    onLoadStart,
    onLoad,
    onError,
    onLoadEnd,
    ...rest
  }) => {
    // Transform FastImage source to RN Image source
    const imageSource = React.useMemo(() => {
      if (!source) return undefined;
      if (typeof source === 'number') return source;
      if (typeof source === 'object' && 'uri' in source) {
        return {
          uri: source.uri,
          headers: source.headers,
        };
      }
      return source as ImageSourcePropType;
    }, [source]);

    return (
      <Image
        {...rest}
        source={imageSource as ImageSourcePropType}
        resizeMode={resizeMode}
        onLoadStart={onLoadStart}
        onLoad={onLoad as any}
        onError={onError as any}
        onLoadEnd={onLoadEnd}
      />
    );
  },
  arePropsEqual,
);

FastImageComponent.displayName = 'FastImage';

// Create the FastImage object with static properties
const FastImage = FastImageComponent as typeof FastImageComponent & {
  resizeMode: {
    contain: 'contain';
    cover: 'cover';
    stretch: 'stretch';
    center: 'center';
  };
  priority: {
    low: 'low';
    normal: 'normal';
    high: 'high';
  };
  cacheControl: {
    immutable: 'immutable';
    web: 'web';
    cacheOnly: 'cacheOnly';
  };
  preload: (sources: FastImageSource[]) => void;
  clearMemoryCache: () => Promise<void>;
  clearDiskCache: () => Promise<void>;
};

// Static properties matching native FastImage API
FastImage.resizeMode = {
  contain: 'contain',
  cover: 'cover',
  stretch: 'stretch',
  center: 'center',
};

FastImage.priority = {
  low: 'low',
  normal: 'normal',
  high: 'high',
};

FastImage.cacheControl = {
  immutable: 'immutable',
  web: 'web',
  cacheOnly: 'cacheOnly',
};

// Preload images for web (uses browser's native preloading)
FastImage.preload = (sources: FastImageSource[]) => {
  if (
    typeof globalThis === 'undefined' ||
    typeof (globalThis as any).Image === 'undefined'
  )
    return;

  sources.forEach(source => {
    if (source?.uri) {
      const img = new (globalThis as any).Image();
      img.src = source.uri;
    }
  });
};

FastImage.clearMemoryCache = async () => {
  // No-op for web - browser manages its own cache
};

FastImage.clearDiskCache = async () => {
  // No-op for web - browser manages its own cache
};

export default FastImage;
